import BreadCrumb from "components/common/BreadCrumb";
import DeleteModal from "components/common/DeleteModal";
import TableContainer from "components/common/TableContainer";
import Avatar from "components/common/Avatar";
import Ellipsis from "components/common/Ellipsis";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import {
  deleteClient,
  getClients,
} from "features/Clients/services/ClientService";
import { Client } from "features/Clients/types/client.types";
import { getClientDisplayName } from "features/Clients/utils/clientDisplayName";
import {
  ClientType,
  ClientTypeLabelMap,
} from "features/Clients/constants/client-organization.constants";
import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { IndexColumn } from "components/common/columns";
import { getDocumentTitle } from "utils/helpers";
import { getClientAvatarUrl } from "features/Clients/utils/clientAvatarUrl";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

const ClientList = () => {
  document.title = getDocumentTitle("Clients");
  const { can } = useAccess();
  const canReadClient = can(Permissions.CLIENT.READ);
  const canUpdateClient = can(Permissions.CLIENT.UPDATE);
  const canDeleteClient = can(Permissions.CLIENT.DELETE);

  const tableRef = useRef<() => void>(null);
  const [client, setClient] = useState<Client | null>(null);

  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const onClickDelete = useCallback((record: Client) => {
    setClient(record);
    setDeleteModal(true);
  }, []);

  const handleDeleteClient = async () => {
    if (client) {
      try {
        const response = await deleteClient(client.id);
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        } else {
          toast.error(response.message);
        }
        setDeleteModal(false);
      } catch (err) {
        console.warn("Error deleting record: ", err);
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? (err as Error).message,
        );
      }
    }
  };

  const columns: ColumnDef<Client>[] = useMemo(
    () => [
      IndexColumn<Client>(),
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const c = row.original;
          const display = getClientDisplayName(c);
          return (
            <div className="d-flex align-items-center gap-2">
              <div className="avatar-sm flex-shrink-0">
                <Avatar name={display} image={getClientAvatarUrl(c.image)} />
              </div>
              <div className="flex-grow-1 min-w-0">
                {canReadClient ? (
                  <Link
                    to={`/clients/${c.id}`}
                    className="fw-semibold text-body text-decoration-none"
                  >
                    <Ellipsis>{display}</Ellipsis>
                  </Link>
                ) : (
                  <span className="fw-semibold text-body">
                    <Ellipsis>{display}</Ellipsis>
                  </span>
                )}
                {c.code ? (
                  <span className="text-muted small">{c.code}</span>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        header: "Type",
        accessorKey: "type",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const t = row.original.type as ClientType | null | undefined;
          if (!t || !ClientTypeLabelMap[t as ClientType]) {
            return <span className="text-muted">—</span>;
          }
          const colorMap: Record<ClientType, string> = {
            [ClientType.GOVERNMENT]: "primary",
            [ClientType.PRIVATE]: "success",
            [ClientType.INDUSTRIAL]: "warning",
          };
          return (
            <span
              className={`badge bg-${colorMap[t as ClientType]}-subtle text-${colorMap[t as ClientType]} border border-${colorMap[t as ClientType]}-subtle`}
            >
              {ClientTypeLabelMap[t as ClientType]}
            </span>
          );
        },
      },
      {
        header: "Company",
        accessorKey: "company",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.company ? (
            <Ellipsis>{row.original.company}</Ellipsis>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.email ? (
            <a
              href={`mailto:${row.original.email}`}
              className="text-decoration-none"
            >
              <i className="ri-mail-line me-1 text-muted" />
              {row.original.email}
            </a>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Phone",
        accessorKey: "phone",
        enableColumnFilter: false,
        cell: ({ row }) =>
          row.original.phone ? (
            <a
              href={`tel:${row.original.phone}`}
              className="text-decoration-none"
            >
              <i className="ri-phone-line me-1 text-muted" />
              {row.original.phone}
            </a>
          ) : (
            <span className="text-muted">—</span>
          ),
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          const hasMenuItems =
            canReadClient || canUpdateClient || canDeleteClient;
          if (!hasMenuItems) {
            return <span className="text-muted small">—</span>;
          }

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Client actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {canReadClient ? (
                  <DropdownItem tag={Link} to={`/clients/${record.id}`}>
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {canUpdateClient ? (
                  <DropdownItem tag={Link} to={`/clients/edit/${record.id}`}>
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {canDeleteClient ? (
                  <>
                    {(canReadClient || canUpdateClient) && (
                      <DropdownItem divider />
                    )}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => onClickDelete(record)}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [onClickDelete, canReadClient, canUpdateClient, canDeleteClient],
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteClient}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Clients" pageTitle="Customers" />
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="card-title mb-0">Client List</h5>
              {can(Permissions.CLIENT.CREATE) ? (
                <Link to="/clients/create" className="btn btn-primary">
                  <i className="ri-add-line align-bottom me-1"></i> Add Client
                </Link>
              ) : null}
            </CardHeader>
            <CardBody>
              <TableContainer
                columns={columns}
                fetchApi={getClients}
                pageSize={10}
                globalSearch={true}
                refreshRef={tableRef}
                divClass="table-responsive table-card mb-3"
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light text-muted"
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ClientList;
